import { useState, useEffect, useRef } from 'react';

/**
 * Shared battle-designation state machine for any form that lets an admin
 * flag a video as a battle and pick 1v1/2v2 + explicit sides. Used by both
 * the AdminDashboard VideoModal and the VideoUpload page so both surfaces
 * derive/validate is_battle/battle_format/sides identically.
 *
 * @param {object} formData must have: artist_ids, is_battle, battle_format, sides
 * @param {(next: object) => void} onFormChange
 * @param {Array<{id:(number|string), display_name?:string, username?:string}>} artists
 *        full artist list, used to resolve the artists currently selected
 *        for the battle (passed to TeamAssignmentModal)
 */
export default function useBattleDesignation(formData, onFormChange, artists) {
  const [showTeamModal, setShowTeamModal] = useState(false);
  const lastIdsKeyRef = useRef(null);

  const artistIds = formData.artist_ids || [];
  const battleArtistCount = artistIds.length;

  // Derives battle_format + sides from the CURRENT artist selection whenever
  // is_battle is on or the selection changes:
  //   - exactly 2 artists -> auto 1v1, sides assigned in selection order
  //     (unambiguous -- no extra confirmation needed for a straight 1v1).
  //   - exactly 4 artists -> battle_format/sides are cleared whenever the
  //     selection changes, so a stale 2v2 assignment can never outlive the
  //     roster it was confirmed for; re-confirming via TeamAssignmentModal
  //     is required again. On first mount (editing an existing 2v2 battle)
  //     the already-confirmed format/sides are left alone.
  //   - any other count -> not supported by this UI; format/sides cleared.
  useEffect(() => {
    if (!formData.is_battle) {
      lastIdsKeyRef.current = null;
      return;
    }

    const idsKey = artistIds.join(',');
    const selectionChanged = lastIdsKeyRef.current !== null && lastIdsKeyRef.current !== idsKey;
    lastIdsKeyRef.current = idsKey;

    if (battleArtistCount === 2) {
      const alreadyCorrect =
        formData.battle_format === '1v1' &&
        formData.sides?.['1']?.[0] === artistIds[0] &&
        formData.sides?.['2']?.[0] === artistIds[1];
      if (!alreadyCorrect) {
        onFormChange({
          ...formData,
          battle_format: '1v1',
          sides: { 1: [ artistIds[0] ], 2: [ artistIds[1] ] },
        });
      }
      return;
    }

    if (battleArtistCount === 4) {
      if (selectionChanged || formData.battle_format !== '2v2' || !formData.sides) {
        if (formData.battle_format !== null || formData.sides) {
          onFormChange({ ...formData, battle_format: null, sides: null });
        }
      }
      return;
    }

    if (formData.battle_format !== null || formData.sides) {
      onFormChange({ ...formData, battle_format: null, sides: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ formData.is_battle, artistIds.join(',') ]);

  const handleBattleToggle = (e) => {
    const checked = e.target.checked;
    onFormChange({
      ...formData,
      is_battle: checked,
      battle_format: checked ? formData.battle_format : null,
      sides: checked ? formData.sides : null,
    });
  };

  const selectedBattleArtists = artists.filter((a) => artistIds.includes(a.id.toString()));

  const handleTeamsConfirmed = (sides) => {
    onFormChange({ ...formData, battle_format: '2v2', sides });
    setShowTeamModal(false);
  };

  // A battle is only submittable once it resolves to a valid, confirmed
  // shape: 1v1 (auto) or a CONFIRMED 2v2 (explicit sides from the modal).
  const battleNeedsAttention =
    formData.is_battle &&
    !(
      (battleArtistCount === 2 && formData.battle_format === '1v1') ||
      (battleArtistCount === 4 && formData.battle_format === '2v2' && formData.sides)
    );

  return {
    showTeamModal,
    setShowTeamModal,
    artistIds,
    battleArtistCount,
    handleBattleToggle,
    selectedBattleArtists,
    handleTeamsConfirmed,
    battleNeedsAttention,
  };
}
